import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "@/components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  Bandage,
  Check,
  ChevronRight,
  CircleDot,
  Clock,
  FlaskConical,
  Flame,
  GitBranch,
  GraduationCap,
  Hand,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Palette,
  Plus,
  Radio,
  Scissors,
  Sparkles,
  Syringe,
  TrendingUp,
  Wind,
  X,
  Zap,
  AlertTriangle,
  Sun,
  Droplets,
  Move,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/burns-burn-scar-treatment-mumbai";
const KELOID_PATH = "/keloid-treatment-mumbai";
const SCAR_REVISION_PATH = "/hypertrophic-scar-treatment-in-mumbai";
const LIPO_FAT_GRAFT_PATH = "/liposuction-surgery-mumbai";

const BURN_FAQS = [
  {
    question: "Can burn scars be completely removed?",
    answer:
      "Not reliably, particularly for deep, extensive, or established burn scars. The damaged tissue has been fundamentally altered and a completely normal skin surface cannot be restored surgically or non-surgically. What is achievable is significant improvement: flatter, softer, less symptomatic scars with better color and texture. For contracture scars, restored movement is an achievable and clinically important goal. The degree of improvement depends on the burn depth, scar type, and how long the scar has been present.",
  },
  {
    question: "When should burn scar treatment begin?",
    answer:
      "As early as possible after the burn wound has healed. Silicone therapy starts when the wound is closed. Compression garments are fitted early in the active scar development phase. Laser and steroid treatments begin at 6 to 8 weeks post-healing for most scar types. The active phase of scar formation, typically the first 12 to 18 months after a burn, is when the scar is most responsive to non-surgical treatment. Treating during this window produces better results than starting after the scar has fully matured.",
  },
  {
    question: "How many laser sessions are needed for burn scars?",
    answer:
      "This depends on the scar type, size, location, and age. Most burn scar patients require 4 to 8 laser sessions for meaningful improvement, with each session spaced 4 to 8 weeks apart to allow the treated tissue to respond before the next treatment. Older, deeper scars may need more sessions than recent, superficial ones. Dr. Jain advises on the expected number of sessions during the first consultation after assessing the scar.",
  },
  {
    question: "What is a burn contracture and can it be corrected?",
    answer:
      "A contracture is a burn scar that crosses a joint or skin fold and tightens with healing, restricting the normal range of movement. Common locations include the neck, axilla, elbow, hand, and knee. Contracture release surgery divides the contracted scar band and covers the defect with a skin graft or flap. Post-operative physiotherapy and splinting maintain the gained movement during healing. Correction is effective when surgery is followed by appropriate rehabilitation. The longer a contracture is present without treatment, the more the underlying joint and tendon structures adapt to the restricted position, making full correction more difficult.",
  },
  {
    question: "Is fat grafting effective for burn scars?",
    answer:
      "Yes. Fat grafting is one of the most versatile treatments for established burn scars. It improves scar pliability, volume, surface texture, and skin quality. The growth factors released by grafted fat cells have a regenerative effect on the damaged overlying skin. Multiple sessions are typically needed for optimal improvement and approximately 60 to 80 percent of grafted fat survives long-term. Fat grafting is particularly beneficial for atrophic scars with volume deficit and for scars where surface skin quality is significantly impaired.",
  },
  {
    question: "What is the most important thing to do for a new burn scar?",
    answer:
      "Start silicone therapy as soon as the wound is closed and apply it consistently for at least 6 months. Apply SPF 50 sunscreen to the healing area daily and avoid direct sun exposure during the first 2 years. Both of these low-cost, low-effort interventions have significant evidence supporting their effectiveness for hypertrophic scar prevention and pigmentation control. Starting these early reduces the need for more intensive treatments later.",
  },
  {
    question: "Can old burn scars still be treated?",
    answer:
      "Yes. Mature burn scars that have been present for years still respond to treatment, though more slowly than active scars in their first 18 months. Laser therapy improves the texture, color, and pliability of older scars. Fat grafting improves quality and volume. Contracture release surgery corrects functional restriction regardless of how long the contracture has been present, though longer-established contractures require more intensive post-operative rehabilitation. Treatment of older scars requires realistic expectations and patience, but meaningful improvement is achievable.",
  },
  {
    question: "Are burn scar treatments painful?",
    answer:
      "Pain levels vary by treatment. Silicone therapy and compression are painless. Steroid injections produce a burning pressure sensation during administration, which is managed with topical anesthesia or local anesthetic at the injection site. Laser treatment produces a warm stinging sensation during the procedure, managed with cooling devices and topical numbing cream. Surgical procedures are performed under appropriate anesthesia. Post-procedure soreness after laser and steroid sessions resolves within 24 to 48 hours.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalCondition",
      name: "Burn Injury",
      description:
        "Thermal, electrical, chemical, or radiation injury to skin and deeper tissue; acute management includes wound care, grafting, and flaps; outcomes influence subsequent scar formation.",
      possibleTreatment: ["Wound care", "Skin grafting", "Flap surgery", "Scar management"],
      url: PAGE_URL,
    },
    {
      "@type": "MedicalCondition",
      name: "Burn Scar",
      description:
        "Scarring after burn healing, including hypertrophic, keloid, contracture, pigmentary, and atrophic patterns; managed with multimodal scar therapy and surgery when indicated.",
      possibleTreatment: [
        "Silicone therapy",
        "Laser therapy",
        "Steroid injections",
        "Contracture release surgery",
        "Fat grafting",
      ],
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Contracture release surgery",
      description:
        "Surgical release of burn-related scar contractures with graft or flap coverage and post-operative rehabilitation to restore joint range of motion.",
      procedureType: "SurgicalProcedure",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Skin, subcutaneous tissue, and joints" },
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: BURN_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function BurnJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: "/reconstructive-surgery-in-mumbai" },
    { name: "Burns & Burn Scar Treatment", href: "/burns-burn-scar-treatment-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Reconstructive Surgery</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Burns &amp; Burn Scar Treatment
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Burn Wound Care &amp; Scar Restoration by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Burn Care" icon="🌿" size="hero"  imageSrc={HERO.burnReconstruction} imageAlt="Burn reconstruction treatment"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function IntroSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="intro-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Burns and Burn Scar Treatment</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Burn injuries damage the skin and underlying tissue through heat, electricity, chemicals, or radiation. The severity determines how the burn heals, whether it scars, and what treatment is needed. Minor burns heal without scarring in most cases. Deep burns that damage the full thickness of the skin cannot heal without surgical intervention and leave significant scars that may restrict movement as well as affect appearance.
            </p>
            <p>
              Burn scar management addresses the scars left after burns have healed. The type of scar, whether hypertrophic, keloid, contracture, or pigmentary change, determines which treatments are appropriate and in what sequence. Burn scars are among the most complex scars to treat because they involve multiple layers of skin and underlying tissue, may affect large areas of the body, and frequently cause functional restriction in addition to cosmetic concern.
            </p>
            <p>
              Dr. Vicky Jain provides burn wound care and burn scar management for patients across the spectrum of burn severity and scar complexity. Treatment is planned individually based on the burn depth, the age of the scar, its location, and whether function is affected alongside cosmetic concern.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Burns and Burn Scar Management</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Acute burn treatment and burn scar treatment require different approaches and different timescales. Early burn care focuses on preventing infection, promoting healing, and minimizing the depth of tissue damage that ultimately determines scar severity. The better the acute burn is managed, the better the long-term scar outcome. Dr. Jain manages acute burns with appropriate dressing selection, wound care protocols, and early surgical intervention when skin grafting is needed for deep burns.
              </p>
              <p>
                Burn scar management begins as soon as the burn wound has closed and continues for months to years depending on scar behavior. The most effective approach addresses the scar at multiple levels simultaneously: silicone therapy for surface hydration and collagen remodeling, steroid injections for active hypertrophic or keloid scarring, laser therapy for pigmentation and texture, and surgery when contractures restrict movement or when scar revision is needed for functional or cosmetic restoration. No single treatment addresses all aspects of a burn scar. Dr. Jain designs a sequenced plan that works through these levels systematically.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const FIRST_AID_DO = [
  "Cool the burn under cool running water for 10 to 20 minutes. Not cold water or ice, which cause additional tissue damage.",
  "Remove tight clothing, jewelry, or anything constricting near the burn before swelling makes this difficult.",
  "Cover the cooled burn with a clean, non-fluffy cloth or cling film to protect from contamination.",
  "Seek medical attention for any burn larger than the patient's palm, any burn on the face, hands, feet, genitals, or joints, any full thickness burn, electrical burns, or chemical burns.",
];

const FIRST_AID_DONT = [
  "Do not apply ice directly to a burn. Ice causes vasoconstriction and frostbite that worsens tissue damage.",
  "Do not apply toothpaste, oil, butter, egg white, or any home remedy. These introduce infection and retain heat.",
  "Do not burst blisters. Intact blisters protect the underlying healing tissue from infection.",
  "Do not use cotton wool directly on a burn. The fibers adhere to the wound surface.",
];

function FirstAidSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20" data-testid="first-aid-section" aria-labelledby="first-aid-heading">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 id="first-aid-heading" className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center max-w-4xl mx-auto">
          Immediate First Aid for Burns: What to Do and What to Avoid
        </h2>
        <Alert className="rounded-none border-2 border-gold/50 bg-white max-w-5xl mx-auto py-6 px-4 md:px-6">
          <AlertTriangle className="h-5 w-5 text-gold" aria-hidden />
          <AlertTitle className="font-serif text-lg text-premium-purple pl-7 mb-4">Urgent first aid guidance</AlertTitle>
          <AlertDescription className="pl-7 text-deep-navy/80 space-y-6">
            <p className="leading-relaxed">
              Correct first aid in the first minutes after a burn significantly affects how the burn heals and how severe the resulting scar will be. The following steps should be applied immediately to any burn before seeking medical attention.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-serif text-lg text-premium-purple mb-4">What to do immediately</h3>
                <ul className="space-y-3">
                  {FIRST_AID_DO.map((line) => (
                    <li key={line} className="flex items-start gap-3 text-deep-navy/70 leading-relaxed">
                      <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" aria-hidden />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-serif text-lg text-premium-purple mb-4">What not to do</h3>
                <ul className="space-y-3">
                  {FIRST_AID_DONT.map((line) => (
                    <li key={line} className="flex items-start gap-3 text-deep-navy/70 leading-relaxed">
                      <X className="w-5 h-5 text-deep-navy/40 flex-shrink-0 mt-0.5" aria-hidden />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="leading-relaxed pt-2 border-t border-deep-navy/10">
              Electrical burns and chemical burns require additional specific management beyond standard first aid. Electrical burns may appear minor on the surface but cause deep tissue damage along the current path. Chemical burns require prolonged water irrigation for at least 20 minutes regardless of the chemical involved, with care taken not to spread the chemical to unaffected skin. Both require urgent medical assessment.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    </section>
  );
}

function TypesOfBurnsSection() {
  const cards = [
    {
      title: "First-Degree Burns (Superficial)",
      icon: Sun,
      copy: "Affect only the outer layer of skin. Cause redness, pain, and mild swelling. No blistering. Heal within 7 to 10 days without scarring in most cases. Treated with cooling, gentle moisturization, and pain relief. Do not require medical or surgical intervention in most cases.",
    },
    {
      title: "Second-Degree Burns (Partial Thickness)",
      icon: Droplets,
      copy: "Extend into the deeper layers of the skin. Cause blistering, significant pain, and a moist, weeping wound surface. Superficial second-degree burns heal within 2 to 3 weeks with good wound care. Deep second-degree burns take longer and often scar. Some deep second-degree burns benefit from early skin grafting to reduce healing time and improve scar outcome.",
    },
    {
      title: "Third-Degree Burns (Full Thickness)",
      icon: Flame,
      copy: "Destroy the full thickness of the skin. The wound surface appears white, waxy, or charred. There is paradoxically less pain than second-degree burns because nerve endings have been destroyed. Full-thickness burns cannot heal without surgical intervention. Skin grafting or flap surgery is required to close the wound.",
    },
    {
      title: "Electrical Burns",
      icon: Zap,
      copy: "Caused by electrical current passing through the body. The entry and exit wounds may appear small but the internal damage along the current path can be extensive. Cardiac monitoring is required. Deep tissue damage often becomes apparent over 24 to 72 hours as the zone of injury declares itself.",
    },
    {
      title: "Chemical Burns",
      icon: FlaskConical,
      copy: "Caused by contact with acids, alkalis, or other corrosive chemicals. Continue to damage tissue until the chemical is completely removed or neutralized. Require prolonged water irrigation as immediate first aid. The depth of injury depends on the chemical concentration, contact time, and whether irrigation was prompt.",
    },
    {
      title: "Radiation Burns",
      icon: Radio,
      copy: "Most commonly caused by prolonged ultraviolet exposure in the clinical setting or by radiotherapy treatment. Radiotherapy burns can develop weeks after treatment and range from superficial skin changes to deep tissue damage requiring reconstructive surgery in severe cases.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="types-of-burns-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types of Burns and Their Treatment</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const ACUTE_TREATMENT = [
  {
    title: "Medical Management",
    icon: Bandage,
    body: "Appropriate for first and superficial second-degree burns. Dressing selection is the most important clinical decision in acute burn management. The right dressing maintains a moist wound environment, prevents infection, manages exudate, and minimizes pain at dressing changes. Antibiotic cream is applied to infected or at-risk burns. Pain management is an integral part of acute burn care, particularly at dressing changes.",
  },
  {
    title: "Skin Grafting",
    icon: Layers,
    body: "Required for full-thickness and deep second-degree burns that will not heal adequately without surgical closure. A thin layer of skin is harvested from a donor site on the patient's own body, most commonly the thigh, and applied to the burn wound after debridement. The grafted skin establishes a blood supply from the wound bed and heals in place. The donor site heals within 2 to 3 weeks. Skin grafting is performed in an operating theatre under general anesthesia.",
  },
  {
    title: "Flap Surgery",
    icon: GitBranch,
    body: "Used when the burn wound bed is too poor to support a skin graft, when deeper structures including tendon, bone, or joint have been exposed, or when a more complex reconstruction is needed to restore function alongside wound closure. Local or distant tissue flaps bring their own blood supply, making them appropriate for wound beds that cannot support graft take.",
  },
  {
    title: "Vacuum-Assisted Closure (VAC)",
    icon: Wind,
    body: "Negative pressure wound therapy applied to prepare burn wound beds for grafting, secure skin grafts during the take period, or manage complex wounds that have not healed with standard dressing. VAC dressings create a controlled negative pressure environment that promotes granulation tissue formation and reduces wound infection.",
  },
];

function AcuteBurnTreatmentSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="acute-burn-treatment-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Acute Burn Treatment Options</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {ACUTE_TREATMENT.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0" aria-hidden>
                  <item.icon className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
                  <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BurnScarTypesSection() {
  const cards = [
    {
      title: "Hypertrophic Scars",
      icon: TrendingUp,
      copy: "The most common burn scar type. Raised, red, firm scars that remain within the burn wound boundary. Most hypertrophic burn scars improve over 12 to 18 months without treatment, though this process is accelerated significantly by silicone therapy, compression, and steroid injections for active scars.",
    },
    {
      title: "Keloid Scars",
      icon: CircleDot,
      copy: "Overgrow beyond the burn wound boundary into surrounding normal skin. Do not regress spontaneously. Require active combination treatment including steroid injections, silicone therapy, and in selected cases surgical excision with adjuvant treatment. Less common than hypertrophic scarring in burn patients but more difficult to treat.",
    },
    {
      title: "Contracture Scars",
      icon: Move,
      copy: "Form when burn scars cross joint surfaces or skin fold areas and tighten with healing, restricting normal movement. Common across the neck, axilla, elbow, and hand in burns affecting these areas. The functional restriction caused by contractures can be significant, limiting ability to work, care for oneself, or perform daily activities. Contracture release surgery is the primary treatment.",
    },
    {
      title: "Pigmentation Changes",
      icon: Palette,
      copy: "Healed burns frequently produce areas of hyperpigmentation, where the skin is darker than surrounding unburned skin, or hypopigmentation, where melanocytes have been destroyed and the skin is lighter. Pigmentation changes are among the most visible and persistent features of burn scars, particularly in patients with darker skin tones.",
    },
    {
      title: "Atrophic Scars",
      icon: Minus,
      copy: "Thin, fragile scar tissue that sits below the level of the surrounding skin. More common in burns that have healed slowly through secondary intention. The thin scar is prone to breakdown with minor trauma. Fat grafting improves the volume, quality, and durability of atrophic burn scars.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="burn-scar-types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types of Burn Scars</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const BURN_SCAR_TREATMENT = [
  {
    title: "Silicone Gel and Sheeting",
    icon: Sparkles,
    body: "The first-line treatment for hypertrophic burn scars. Applied continuously to the healed burn scar, silicone hydrates the scar tissue, reduces collagen overproduction, and flattens the scar over 3 to 6 months of consistent use. Most effective when started as soon as the burn wound has closed and continued for at least 6 months. Requires patient compliance for meaningful benefit.",
  },
  {
    title: "Compression Therapy",
    icon: Hand,
    body: "Pressure garments worn over burn-affected areas apply continuous pressure that flattens hypertrophic scars and reduces their blood supply. Particularly important for burns of the hands, face, and trunk. Custom-fitted garments are worn for 23 hours per day for up to 2 years in active scar management. Used alongside silicone therapy for hypertrophic and contracture-prone scars.",
  },
  {
    title: "Steroid Injections",
    icon: Syringe,
    body: "Intralesional triamcinolone injections reduce active hypertrophic and keloid burn scars by suppressing collagen production and reducing the inflammatory activity within the scar. Multiple sessions are required. Most effective for raised, itchy, actively growing scars. Combined with silicone therapy for synergistic effect.",
  },
  {
    title: "Laser Therapy",
    icon: Zap,
    body: "Multiple laser types address different aspects of burn scars. Pulsed dye laser reduces redness and scar vascularity. Ablative fractional laser improves scar texture, surface quality, and flexibility. Non-ablative fractional laser improves pigmentation and skin quality with less downtime than ablative techniques. Laser treatment requires multiple sessions and is most effective when combined with other scar management modalities.",
  },
  {
    title: "Scar Revision Surgery",
    icon: Scissors,
    body: "For burn scars with poor orientation, excessive width, or distortion of surrounding anatomy, surgical revision improves the scar's appearance and position. Techniques include excision and re-closure, Z-plasty to redirect scar tension, and W-plasty to break up a linear scar into a less visible irregular pattern.",
  },
  {
    title: "Contracture Release Surgery",
    icon: Move,
    body: "Surgical release of burn contractures that restrict movement. The contracted scar band is divided and the resulting defect is covered with a skin graft or local flap. Allows restoration of movement that the contracture was preventing. Post-operative physiotherapy and splinting are essential to maintain the gained range of movement during healing.",
  },
  {
    title: "Fat Grafting",
    icon: Layers,
    body: "Autologous fat is harvested by liposuction from a donor area and injected into the burn scar and surrounding tissue. Fat grafting improves scar pliability, skin quality, surface texture, and volume in atrophic burn scars. Growth factors released by the grafted fat have a regenerative effect on the damaged skin overlying the scar. Multiple sessions may be needed for optimal improvement.",
  },
];

function BurnScarTreatmentSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="burn-scar-treatment-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">The Procedure</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Burn Scar Treatment Options</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {BURN_SCAR_TREATMENT.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0" aria-hidden>
                  <item.icon className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
                  <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ExpectationsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="expectations-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">What to Expect</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What to Expect from Burn Scar Treatment</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Burn scar improvement is gradual and cumulative. No single treatment session produces a finished result. Silicone therapy and steroid injections improve scars over months of consistent use. Laser treatment requires a course of sessions spaced 4 to 6 weeks apart with improvement building between each session. Surgical procedures produce immediate structural change but the final result is only visible after the surgical wound has healed and matured.
            </p>
            <p>
              Complete elimination of a burn scar is not a realistic expectation for established, deep, or extensive scars. The goal is significant improvement: a scar that is flatter, softer, less symptomatic, better in color, and in the case of contractures, no longer restricting movement. Most patients achieve meaningful improvement that makes a real difference to appearance and function. The degree of improvement depends on the burn depth, the scar type, how long the scar has been present, the patient's skin type, and their commitment to the non-surgical components of management such as silicone therapy and sun protection.
            </p>
            <p>
              Sun protection throughout the treatment period and beyond is one of the most important factors in burn scar outcome. UV exposure causes permanent hyperpigmentation of healing scar tissue and worsens the color mismatch between scar and surrounding skin. SPF 50 sunscreen applied daily and physical protection with clothing are essential, particularly during the first 2 years after a burn.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoShouldSeekSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-should-seek-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Who Should Seek Burn Scar Treatment?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any patient with a burn scar causing cosmetic concern, functional restriction, pain, itching, or psychological distress is appropriate for consultation. There is no minimum scar age required before treatment can begin. Silicone therapy and compression start as soon as the burn wound has closed, which is the earliest possible point in scar management. Laser therapy and steroid injections begin once the scar is sufficiently healed to tolerate the treatment, typically 6 to 8 weeks after wound closure.
            </p>
            <p>
              Patients with established burn scars that have been present for years are still candidates for treatment. Older scars respond more slowly than active scars but significant improvement is achievable. Patients with contractures affecting movement should seek assessment as a priority, as functional restriction from contractures worsens with time and is more difficult to reverse the longer it has been present. Patients who have had burns managed elsewhere and are dissatisfied with their scar outcome are appropriate for reassessment and development of a comprehensive scar management plan.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Burn" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {BURN_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`burn-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Burns &amp; Scar Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses the burn history, scar type, location, and whether functional restriction is present to design a treatment plan that addresses your specific scar with the most appropriate combination of non-surgical and surgical approaches. Realistic outcomes are discussed honestly. You leave with a clear, sequenced treatment plan and an understanding of what each step involves and what improvement is achievable.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Keloid Treatment",
      description: "Comprehensive management for keloid scars arising from burn injuries.",
      href: KELOID_PATH,
    },
    {
      name: "Scar Revision",
      description: "Surgical improvement of burn scars with poor orientation, width, or cosmetic impact.",
      href: SCAR_REVISION_PATH,
    },
    {
      name: "Liposuction & Fat Grafting",
      description: "Autologous fat transfer to improve burn scar quality and restore volume in atrophic scars.",
      href: LIPO_FAT_GRAFT_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BurnReconstructionPage() {
  return (
    <>
      <SEO
        title="Burns & Burn Scar Treatment | Dr. Vicky Jain"
        description="Burn injuries and burn scars affecting appearance, movement, or confidence. Dr. Vicky Jain provides advanced burn care and scar treatment. Book today."
        canonical="/burns-burn-scar-treatment-mumbai"
      />
      <BurnJsonLd />
      <Breadcrumb />
      <HeroSection />
      <IntroSection />
      <DrJainApproachSection />
      <FirstAidSection />
      <TypesOfBurnsSection />
      <AcuteBurnTreatmentSection />
      <BurnScarTypesSection />
      <BurnScarTreatmentSection />
      <ExpectationsSection />
      <WhoShouldSeekSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
